import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Modal, Input, Textarea } from "./Leads";
import { BookOpen, Edit3, Save, Sparkles } from "lucide-react";
import { toast } from "sonner";

const TYPE_PILL = {
  EC: "bg-emerald-50 text-emerald-800 border-emerald-200",
  ECMP: "bg-amber-50 text-amber-800 border-amber-200",
  Consent: "bg-sky-50 text-sky-800 border-sky-200",
  MEP: "bg-stone-100 text-stone-700 border-stone-200",
  CRZ: "bg-teal-50 text-teal-800 border-teal-200",
};

export default function Repository() {
  const [tab, setTab] = useState("Proposal Templates");
  const [templates, setTemplates] = useState([]);
  const [checklists, setChecklists] = useState({ templates: {}, stages: {} });
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [t, c, p] = await Promise.all([
      api.get("/templates/proposals"),
      api.get("/templates/checklists"),
      api.get("/products"),
    ]);
    setTemplates(t.data);
    setChecklists(c.data);
    setProducts(p.data);
  };
  useEffect(() => { load(); }, []);

  const saveTemplate = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/templates/proposals/${editing.key}`, {
        ...editing,
        deliverables: typeof editing.deliverables === "string"
          ? editing.deliverables.split("\n").map((s) => s.trim()).filter(Boolean)
          : editing.deliverables,
      });
      toast.success(`${editing.key} template updated`);
      setEditing(null);
      load();
    } catch { toast.error("Save failed"); }
  };

  const openEdit = (t) => setEditing({
    ...t,
    deliverables: Array.isArray(t.deliverables) ? t.deliverables.join("\n") : (t.deliverables || ""),
  });

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <p className="text-xs uppercase tracking-[0.22em] text-emerald-700 font-semibold mb-2">Standards</p>
        <h1 className="text-4xl font-bold tracking-tight text-stone-900" style={{ fontFamily: "Bricolage Grotesque" }}>Knowledge Repository</h1>
        <p className="text-stone-500 mt-2">Manage standard proposal templates and document checklists.</p>
      </div>

      <div className="border-b border-stone-200">
        <div className="flex gap-1">
          {["Proposal Templates", "Document Checklists", "Workflow Stages", "Product Catalog"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`repo-tab-${t}`}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition ${
                tab === t ? "border-emerald-700 text-emerald-800" : "border-transparent text-stone-500 hover:text-stone-900"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {tab === "Proposal Templates" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map((t) => (
            <div key={t.key} data-testid={`tpl-${t.key}`} className="bg-white border border-stone-200 rounded-xl p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-emerald-700" />
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold border ${TYPE_PILL[t.key] || TYPE_PILL.EC}`}>
                    {t.key}
                  </span>
                </div>
                <button onClick={() => openEdit(t)} data-testid={`edit-tpl-${t.key}`} className="text-xs flex items-center gap-1.5 text-stone-600 hover:text-emerald-700 transition">
                  <Edit3 className="w-3.5 h-3.5" /> Edit
                </button>
              </div>
              <div className="font-semibold text-stone-900">{t.label}</div>
              <div className="text-xs text-stone-500 mt-1">Default service type: {t.service_type}</div>
              <div className="mt-4 pt-4 border-t border-stone-100 space-y-2 text-xs">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold mb-1">Deliverables</div>
                  <ul className="space-y-0.5">
                    {(t.deliverables || []).slice(0, 4).map((d) => (
                      <li key={d} className="text-stone-700 flex items-start gap-1.5"><span className="text-emerald-600">▸</span>{d}</li>
                    ))}
                    {(t.deliverables || []).length > 4 && <li className="text-stone-400 ml-3">+ {t.deliverables.length - 4} more</li>}
                  </ul>
                </div>
                <div className="pt-2">
                  <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold mb-1">Payment Terms</div>
                  <div className="text-stone-700">{t.payment_terms}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "Document Checklists" && (
        <div className="space-y-4">
          {Object.entries(checklists.templates || {}).map(([type, docs]) => (
            <div key={type} className="bg-white border border-stone-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="font-semibold text-stone-900">{type}</div>
                <span className="text-xs bg-stone-100 px-2 py-0.5 rounded-full">{docs.length} items</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                {docs.map((d) => (
                  <div key={d} className="flex items-center gap-2 text-stone-700"><span className="text-emerald-600">▸</span>{d}</div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "Workflow Stages" && (
        <div className="space-y-4">
          {Object.entries(checklists.stages || {}).map(([type, stages]) => (
            <div key={type} className="bg-white border border-stone-200 rounded-xl p-5">
              <div className="font-semibold text-stone-900 mb-3">{type}</div>
              <div className="flex flex-wrap gap-2">
                {stages.map((s, i) => (
                  <React.Fragment key={s}>
                    <span className="text-xs bg-stone-50 border border-stone-200 px-3 py-1.5 rounded-full text-stone-700">{i + 1}. {s}</span>
                    {i < stages.length - 1 && <span className="text-stone-300 self-center">→</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "Product Catalog" && (
        <div className="space-y-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-900">
            <b>PTESPL Product Suite</b> — {products.length} solutions across Treatment Plants, Monitoring, Pollution Control, Waste and Automation. Reference these when scoping Environmental Systems projects.
          </div>
          {Object.entries(
            products.reduce((acc, p) => {
              (acc[p.category] = acc[p.category] || []).push(p);
              return acc;
            }, {})
          ).map(([category, list]) => (
            <div key={category} className="bg-white border border-stone-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-700 font-semibold">{category}</div>
                  <div className="font-semibold text-stone-900 mt-1">{list.length} product{list.length > 1 ? "s" : ""}</div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {list.map((p) => (
                  <div key={p.key} data-testid={`product-${p.key}`} className="border border-stone-200 rounded-lg p-4 hover:border-emerald-700 hover:bg-emerald-50/30 transition">
                    <div className="font-semibold text-stone-900">{p.name}</div>
                    <div className="text-xs text-emerald-700 font-medium mt-0.5">{p.tagline}</div>
                    <p className="text-xs text-stone-600 mt-2 leading-relaxed">{p.description}</p>
                    <div className="mt-3 pt-3 border-t border-stone-100">
                      <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold mb-1">Key Features</div>
                      <ul className="space-y-0.5">
                        {(p.features || []).slice(0, 4).map((f) => (
                          <li key={f} className="text-xs text-stone-700 flex items-start gap-1.5"><span className="text-emerald-600">▸</span>{f}</li>
                        ))}
                        {(p.features || []).length > 4 && <li className="text-[10px] text-stone-400 ml-3">+ {p.features.length - 4} more</li>}
                      </ul>
                    </div>
                    {p.applications && p.applications.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {p.applications.slice(0, 4).map((a) => (
                          <span key={a} className="text-[10px] bg-stone-100 text-stone-700 px-2 py-0.5 rounded-full">{a}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <Modal title={`Edit ${editing.key} Template`} onClose={() => setEditing(null)}>
          <form onSubmit={saveTemplate} className="space-y-3">
            <div className="bg-emerald-50/50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900 flex items-start gap-2">
              <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Changes here will apply to <b>new</b> proposals created from this template.</span>
            </div>
            <Input label="Label" value={editing.label} onChange={(v) => setEditing({ ...editing, label: v })} testid="tpl-label" />
            <Input label="Service Type" value={editing.service_type} onChange={(v) => setEditing({ ...editing, service_type: v })} testid="tpl-service" />
            <Textarea label="Scope" value={editing.scope} onChange={(v) => setEditing({ ...editing, scope: v })} testid="tpl-scope" />
            <Textarea label="Deliverables (one per line)" value={editing.deliverables} onChange={(v) => setEditing({ ...editing, deliverables: v })} testid="tpl-deliverables" />
            <Textarea label="Inclusions" value={editing.inclusions} onChange={(v) => setEditing({ ...editing, inclusions: v })} testid="tpl-inclusions" />
            <Textarea label="Exclusions" value={editing.exclusions} onChange={(v) => setEditing({ ...editing, exclusions: v })} testid="tpl-exclusions" />
            <Input label="Payment Terms" value={editing.payment_terms} onChange={(v) => setEditing({ ...editing, payment_terms: v })} testid="tpl-terms" />
            <Input label="Validity (days)" type="number" value={editing.validity_days} onChange={(v) => setEditing({ ...editing, validity_days: Number(v) })} testid="tpl-validity" />
            <button type="submit" data-testid="tpl-save" className="w-full flex items-center justify-center gap-2 bg-emerald-700 text-white py-2.5 rounded-lg font-medium">
              <Save className="w-4 h-4" /> Save Template
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}
